import React from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useLocation, Link } from "react-router-dom";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {
  DashboardOutlined,
  PeopleOutline,
  ApartmentOutlined,
  SavingsOutlined,
  // VerifiedUserOutlined,
  SettingsOutlined,
  AccountBalanceOutlined,
} from "@mui/icons-material";

import {
  dashboardShell,
  DRAWER_WIDTH,
  DRAWER_WIDTH_COLLAPSED,
  SIDEBAR_TRANSITION_MS,
  ADMIN_HEADER_HEIGHT_PX,
} from "../../theme/dashboardShell";
import TokenitizeName from "../layout/TokenitizeName";

const { sidebar: shell } = dashboardShell;

const NAV = [
  { label: "Dashboard", to: "/admin", icon: <DashboardOutlined />, end: true },
  { label: "Users", to: "/admin/users", icon: <PeopleOutline /> },
  { label: "Assets", to: "/admin/assets", icon: <ApartmentOutlined /> },
  { label: "Staking Pools", to: "/admin/staking-pools", icon: <SavingsOutlined /> },
  { label: "Loans", to: "/admin/loans", icon: <AccountBalanceOutlined /> },
  // {
  //   label: "Verifications",
  //   to: "/admin/verifications",
  //   icon: <VerifiedUserOutlined />,
  // },
  { label: "Settings", to: "/admin/settings", icon: <SettingsOutlined /> },
  // { label: "Analytics", to: "/admin/analytics", icon: <BarChartOutlined /> },
];

export default function AdminSidebar({ collapsed = false, onToggleCollapse }) {
  const { pathname } = useLocation();
  const width = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <Box
      component="nav"
      aria-label="Admin main menu"
      sx={{
        width,
        height: "100dvh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 1200,
        bgcolor: shell.bg,
        color: shell.text,
        display: "flex",
        flexDirection: "column",
        borderRight: shell.border,
        transition: (t) =>
          t.transitions.create("width", {
            easing: t.transitions.easing.sharp,
            duration: SIDEBAR_TRANSITION_MS,
          }),
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: collapsed ? "column" : "row",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          gap: collapsed ? 0.5 : 0,
          px: collapsed ? 0.75 : 1.5,
          py: 1.25,
          bgcolor: shell.bgHeader,
          borderBottom: shell.border,
          minHeight: ADMIN_HEADER_HEIGHT_PX,
          maxHeight: ADMIN_HEADER_HEIGHT_PX,
        }}
      >
        {!collapsed && <TokenitizeName height={32} sx={{ flex: 1, minWidth: 0 }} />}
        <IconButton
          size="small"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          sx={{
            color: shell.textMuted,
            flexShrink: 0,
            border: "1px solid rgba(148, 163, 184, 0.25)",
            borderRadius: 1,
            "&:hover": { bgcolor: "rgba(148, 163, 184, 0.12)", color: shell.text },
          }}
        >
          {collapsed ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ViewSidebarOutlinedIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Box>

      {!collapsed && (
        <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: shell.textMuted,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            Main menu
          </Typography>
        </Box>
      )}

      <List sx={{ px: collapsed ? 0.75 : 1, py: 1, flex: 1 }}>
        {NAV.map((item) => {
          const active = item.end
            ? pathname === item.to
            : pathname === item.to || pathname.startsWith(`${item.to}/`);
          const button = (
            <ListItemButton
              key={item.label}
              component={Link}
              to={item.to}
              sx={{
                mb: collapsed ? 0.75 : 1.25,
                borderRadius: collapsed ? shell.navItemRadiusCollapsed : shell.navItemRadiusExpanded,
                px: collapsed ? 0 : 1.5,
                py: collapsed ? 1 : 1.1,
                justifyContent: collapsed ? "center" : "flex-start",
                minHeight: shell.navItemMinHeight,
                boxShadow: active ? shell.itemActiveShadow : "none",
                border: shell.itemActiveBorder,
                bgcolor: active ? shell.itemActive : "transparent",
                color: active ? shell.itemActiveText : shell.textMuted,
                transition: (t) => t.transitions.create(["background-color", "color"], { duration: 160 }),
                "&:hover": {
                  bgcolor: active ? shell.itemActiveHover : shell.itemHover,
                  boxShadow: active ? shell.itemActiveShadow : "none",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 36,
                  mr: collapsed ? 0 : 0,
                  justifyContent: "center",
                  color: active ? shell.iconActive : shell.iconMuted,
                  "& .MuiSvgIcon-root": { fontSize: 22 },
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: active ? 600 : 500,
                    color: active ? shell.itemActiveText : shell.textMuted,
                    letterSpacing: 0.01,
                  }}
                />
              )}
            </ListItemButton>
          );

          if (collapsed) {
            return (
              <Tooltip key={item.label} title={item.label} placement="right" arrow>
                <Box component="span" sx={{ display: "block" }}>
                  {button}
                </Box>
              </Tooltip>
            );
          }
          return button;
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* {!collapsed && (
        <>
          <Divider sx={{ borderColor: shell.dividerMuted }} />
          <Box sx={{ px: 2, py: 2, bgcolor: shell.bgHeader, borderTop: shell.border }}>
            <Typography
              variant="caption"
              sx={{ color: shell.textMuted, display: "block", mb: 1, fontWeight: 600 }}
            >
              Platform
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label="Regulated"
                sx={{
                  bgcolor: shellAdmin.platformChipRegulatedBg,
                  color: shellAdmin.platformChipRegulatedColor,
                  fontSize: 11,
                  fontWeight: 600,
                  height: 24,
                }}
              />
              <Chip
                size="small"
                label="Verified"
                sx={{
                  bgcolor: shellAdmin.platformChipVerifiedBg,
                  color: shellAdmin.platformChipVerifiedColor,
                  fontSize: 11,
                  fontWeight: 600,
                  height: 24,
                }}
              />
            </Box>
          </Box>
        </>
      )} */}
    </Box>
  );
}
